/**
 * Promo section countdown timer.
 * Modes:
 * - fixed: data-end as ISO-like local datetime (stops when expired)
 * - loop: counts down to local midnight × N days, then restarts
 */
class PromoCountdown extends HTMLElement {
  /** @type {number | null} */
  #interval = null;

  /** @type {HTMLElement | null} */
  #days = null;

  /** @type {HTMLElement | null} */
  #hours = null;

  /** @type {HTMLElement | null} */
  #minutes = null;

  /** @type {HTMLElement | null} */
  #seconds = null;

  /** @type {HTMLElement | null} */
  #units = null;

  /** @type {HTMLElement | null} */
  #expired = null;

  connectedCallback() {
    this.#days = this.querySelector('[data-days]');
    this.#hours = this.querySelector('[data-hours]');
    this.#minutes = this.querySelector('[data-minutes]');
    this.#seconds = this.querySelector('[data-seconds]');
    this.#units = this.querySelector('[data-units]');
    this.#expired = this.querySelector('[data-expired]');

    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  #tick() {
    const mode = this.dataset.mode || 'fixed';

    if (mode === 'loop') {
      this.#tickLoop();
      return;
    }

    this.#tickFixed();
  }

  /**
   * Countdown to local midnight after N calendar days.
   * Example: 1 day at 00:06 → ~23:54 left; at midnight restarts for the next day.
   */
  #tickLoop() {
    const days = Math.max(1, parseInt(this.dataset.loopDays || '1', 10) || 1);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let end = new Date(startOfToday.getTime() + days * 24 * 60 * 60 * 1000);

    // Exactly at midnight (or past) → roll to the next period
    if (end.getTime() <= now.getTime()) {
      end = new Date(end.getTime() + days * 24 * 60 * 60 * 1000);
    }

    this.#render(end.getTime() - now.getTime());
  }

  #tickFixed() {
    const endRaw = this.dataset.end;
    if (!endRaw) {
      this.#showExpired();
      return;
    }

    // Support "YYYY-MM-DD HH:mm" and ISO strings
    const normalized = endRaw.includes('T') ? endRaw : endRaw.replace(' ', 'T');
    const end = new Date(normalized).getTime();

    if (Number.isNaN(end)) {
      this.#showExpired();
      return;
    }

    const diff = end - Date.now();

    if (diff <= 0) {
      this.#showExpired();
      return;
    }

    this.#render(diff);
  }

  /**
   * @param {number} diffMs
   */
  #render(diffMs) {
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#set(this.#days, days);
    this.#set(this.#hours, hours);
    this.#set(this.#minutes, minutes);
    this.#set(this.#seconds, seconds);

    if (this.#units) this.#units.hidden = false;
    if (this.#expired) this.#expired.hidden = true;
  }

  /**
   * @param {HTMLElement | null} el
   * @param {number} value
   */
  #set(el, value) {
    if (!el) return;
    el.textContent = String(value).padStart(2, '0');
  }

  #showExpired() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }

    if (this.#units) this.#units.hidden = true;
    if (this.#expired) {
      this.#expired.hidden = false;
      this.#expired.textContent = this.dataset.expiredLabel || 'Teklif süresi sona erdi';
    }
  }
}

if (!customElements.get('promo-countdown')) {
  customElements.define('promo-countdown', PromoCountdown);
}
